/**
 * Options as nodes, in motion: after a move the old options that are gone
 * fade out where they were (re-projected into the new camera so the glide
 * carries them), and the new ones pop in. Pure; planned by motion.ts.
 */
import type { Camera } from "./layout";
import type { MotionSnapshot } from "./motion";

export interface SnapshotOption {
  id: string;
  px: number;
  py: number;
  r: number;
  existing: boolean;
  label?: { x: number; y: number; fontSize: number };
}

/** An old option fading out after a move, in the new layout's coordinates. */
export interface GhostOption extends SnapshotOption {
  /** Where its spoke started (the old current word). */
  from: { px: number; py: number } | null;
}

/** Map a pixel position under camera `a` to the same world point under `b`. */
function reproject(a: Camera, b: Camera, p: { px: number; py: number }) {
  const k = b.s / a.s;
  return { px: (p.px - a.tx) * k + b.tx, py: (p.py - a.ty) * k + b.ty };
}

/**
 * After a move: the old options that are gone (not still offered and not the
 * chosen word) fade out where they were; the new ones pop in.
 */
export function optionSwap(
  prev: MotionSnapshot,
  next: MotionSnapshot,
  oldCurrent: string,
): { ghosts: GhostOption[]; born: string[] } | null {
  const before = prev.options ?? [];
  const after = next.options ?? [];
  if (!before.length && !after.length) return null;
  const keep = new Set(after.map((o) => o.id));
  const chosen = next.path[next.path.length - 1];
  const oldCur = prev.pos[oldCurrent];
  const re = (p: { px: number; py: number }) =>
    reproject(prev.camera, next.camera, p);
  const ghosts = before
    .filter((o) => !o.existing && !keep.has(o.id) && o.id !== chosen)
    .map((o) => {
      const p = re(o);
      const lp = o.label && re({ px: o.label.x, py: o.label.y });
      const label = o.label && lp && { ...o.label, x: lp.px, y: lp.py };
      return { ...o, ...p, label, from: oldCur ? re(oldCur) : null };
    });
  const had = new Set(before.map((o) => o.id));
  const born = after.filter((o) => !had.has(o.id)).map((o) => o.id);
  return ghosts.length || born.length ? { ghosts, born } : null;
}
